/**
 * Reports — `docs/API.md` §13, answered as six concrete endpoints at
 * `/analytics/*` instead of the spec's saved-report-definition engine —
 * see `salescrmapi/docs/modules/reports.model.md` §0.
 *
 * Every function shares one response envelope
 * (`{report, breakdown, period, columns, rows, totals, summary, trend?, submissions?}`);
 * the mock fallback below answers the same empty shape for all six rather
 * than one fixture per report, since there's nothing here that a screen
 * needs to fake convincingly the way, say, a lead record does.
 */
import apiClient from './axiosClient.js';
import { withMockFallback } from './mockFallback.js';

function emptyReport(report, breakdown) {
  return () => ({
    report, breakdown: breakdown ?? null,
    period: { from: null, to: null },
    generated_at: new Date().toISOString(),
    columns: [], rows: [], totals: {}, summary: {}, trend: [], submissions: [],
  });
}

/** @param {object} [params] `{ breakdown, from, to, owner_id, territory_id }` */
export function getLeadsReport(params = {}) {
  return withMockFallback('GET /analytics/leads',
    async () => (await apiClient.get('/analytics/leads', { params })).data,
    emptyReport('leads', params.breakdown));
}

/** @param {object} [params] `{ breakdown, from, to, owner_id, territory_id }` */
export function getOpportunitiesReport(params = {}) {
  return withMockFallback('GET /analytics/opportunities',
    async () => (await apiClient.get('/analytics/opportunities', { params })).data,
    emptyReport('opportunities', params.breakdown));
}

/** @param {object} [params] `{ breakdown, from, to, owner_id, kind }` */
export function getActivitiesReport(params = {}) {
  return withMockFallback('GET /analytics/activities',
    async () => (await apiClient.get('/analytics/activities', { params })).data,
    emptyReport('activities', params.breakdown));
}

/** @param {object} [params] `{ breakdown, period, from, to, owner_id, territory_id }` */
export function getRevenueReport(params = {}) {
  return withMockFallback('GET /analytics/revenue',
    async () => (await apiClient.get('/analytics/revenue', { params })).data,
    emptyReport('revenue', params.breakdown));
}

/** @param {object} [params] `{ period }` */
export function getForecastReport(params = {}) {
  return withMockFallback('GET /analytics/forecast',
    async () => (await apiClient.get('/analytics/forecast', { params })).data,
    emptyReport('forecast', null));
}

/** @param {object} [params] `{ from, to }` */
export function getTerritoriesReport(params = {}) {
  return withMockFallback('GET /analytics/territories',
    async () => (await apiClient.get('/analytics/territories', { params })).data,
    emptyReport('territories', null));
}
